// @flow
import React, {type Node, Fragment} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import useToggle from 'utils/hooks/useToggle'
import CircleIcon from 'components/ui/icons/Circle'
import SectionItem from 'components/SideBar/ui/SectionItem'
import ToggleButton from 'components/SideBar/ui/ToggleButton'
import SectionWithAssignments from './SectionWithAssignments'
import type {
  CircleAssignments_assignments as Assignments,
} from './__generated__/CircleAssignments_assignments.graphql'
import type {
  CircleAssignments_circle as Circle,
} from './__generated__/CircleAssignments_circle.graphql'

type Props = $ReadOnly<{
  circle: Circle,
  assignments: Assignments,
}>

const circleIcon = () => <CircleIcon />

function CircleAssignments({circle, assignments}: Props): Node {
  const [isCollapsed, {toggle: toggleCollapsed}] = useToggle(false)

  const chartPath = circle.supportedRole?.chartPath

  const toggleButton = () => (
    <ToggleButton
      isCollapsed={isCollapsed}
      onToggle={toggleCollapsed}
    />
  )

  const nullOnClick = (event: SyntheticMouseEvent<*>) => {
    event.preventDefault()
  }

  return (
    <Fragment>
      <SectionItem
        title={circle.localizedName}
        renderIcon={circleIcon}
        renderActionButton={toggleButton}
        href={chartPath}
        onClick={(!chartPath) ? nullOnClick : undefined}
      />
      {!isCollapsed && (
        <SectionWithAssignments
          assignments={assignments}
        />
      )}
    </Fragment>
  )
}

export default (createFragmentContainer(CircleAssignments, {
  circle: graphql`
    fragment CircleAssignments_circle on Circle {
      localizedName
      supportedRole {
        chartPath
      }
    }
  `,
  assignments: graphql`
    fragment CircleAssignments_assignments on Assignment @relay(plural: true) {
      ...SectionWithAssignments_assignments
    }
  `,
}): RelayFragmentContainer<typeof(CircleAssignments)>)
